import { CRM_ACTIONS, RCXAdapter, type MessageEventData, StartRenderType, RCXMetadata, RCXLogger } from 'rcx_adapter';

import { SecondIframeState } from '../constants';
import {
    setSecondIframeScript,
    updateSecondIframeScript,
    setSecondIframeDigital,
    setScriptUrl,
    resetSecondIframeState,
    updateSecondIframeLoading,
    changeSecondIframeState,
} from '../state/actions';

const LOG_PREFIX = '[rcx_crm_ui][SecondIframeAdapter]';
const SECOND_IFRAME_MESSAGE_TYPE = 'crm_sideCarEvent';
enum DigitalMessageType {
    NEW_MESSAGE = 'smcc:task:updated',
}

export type SecondIframeAdapterOptions = {
    disabledCall?: boolean;
    disableDigital?: boolean;
    CTIType?: string;
    openCTIApp?: () => void;
};

export class SecondIframeAdapter {
    private _scriptWindow: Window;
    private _hasInit = false;
    private _rcxAdapter: RCXAdapter;
    private _dispatch: any;
    private _options: SecondIframeAdapterOptions = {};
    private _chatMap: Map<string, any> = new Map();
    private _curScriptRenderId: string = '';
    private _curType: string = 'script';

    constructor(rcxAdapter: RCXAdapter, options?: SecondIframeAdapterOptions) {
        this._scriptWindow = window;
        this._rcxAdapter = rcxAdapter;
        if (options) {
            this._options = options;
        }
    }

    public start(dispatch: any) {
        if (this._rcxAdapter && !this._hasInit) {
            this._dispatch = dispatch;
            this._addErrorListener();
            this._addRCXListener();
        }
    }

    private _addRCXListener() {
        this._rcxAdapter.onSetRCXMetadata(this._handleMetadata.bind(this));
        this._rcxAdapter.onStartRender(this._handleStartRender.bind(this));
        this._rcxAdapter.onUpdateRender(this._handleUpdate.bind(this));
        this._rcxAdapter.onEndCall(this._handleCallEnd.bind(this));
        this._rcxAdapter.onPendingDisposition(this._handlePendingDisposition.bind(this));
        this._rcxAdapter.onChatDispResponse(this._handleChatDispResponse.bind(this));
        this._rcxAdapter.onChatAccepted(this._handleChatAccepted.bind(this));
        this._rcxAdapter.onIntegrationHasLogout(this._handleLogout.bind(this));
        this._rcxAdapter.onIntegrationHasLogin(this._handleLogin.bind(this));
        this._rcxAdapter.onStopRender(this._handleStopRender.bind(this));
        this._hasInit = true;
    }
    public getCTIType() {
        return this._options.CTIType;
    }

    public analyticsTrack(event: string, data?: any) {
        this._rcxAdapter.analyticsTrack(event, data);
    }

    private _handleMetadata(data: RCXMetadata) {
        if (!this._options.disabledCall && data?.scriptUrl) {
            RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleMetadata]:`, data);
            this._dispatch(setScriptUrl(data?.scriptUrl));
        }
    }

    private _handleChatAccepted(data: any) {
        if (this._options.disableDigital) return;
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleChatAccepted] uii: ${data.uii}`);
        this._chatMap.set(data.uii, { accepted: true });
    }

    private _handleStartRender(data: any) {
        if (
            !this._options.disabledCall &&
            data.type === StartRenderType.SCRIPT &&
            data.renderId &&
            data.params?.scriptId
        ) {
            RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleStartRender_SCRIPT]:`, data);
            if (this._curScriptRenderId !== data.renderId) {
                this._curScriptRenderId = data.renderId;
                this._dispatch(updateSecondIframeLoading(true));
            }

            this._dispatch(setSecondIframeScript({ ...data, scriptId: data.params.scriptId }));
        } else if (!this._options.disableDigital && data.type === StartRenderType.DIGITAL && data.renderId) {
            RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleStartRender_DIGITAL]:`, data);
            if (this._chatMap.get(data.renderId)?.accepted) {
                this._dispatch(setSecondIframeDigital(data));
                this._onDigitalStart();
            }
        }
        this._curType = data.type;
    }

    private _handleUpdate(data: any) {
        if (this._options.disabledCall) return;
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleUpdate_SCRIPT]:`, data);
        const { renderId, params = {} } = data;
        if (params.scriptId) {
            this._dispatch(updateSecondIframeScript({ renderId, scriptId: params.scriptId }));
        }
    }

    private _handleCallEnd(data: any) {
        if (this._options.disabledCall) return;
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleCallEnd] uii: ${data.uii}`);
        const { uii } = data;
        this._dispatch(
            updateSecondIframeScript({
                renderId: uii,
                isEnd: true,
                submitted: !data.pendingDisp,
            })
        );
    }

    private _handleLogout() {
        this.resetState();
        // do not removeMessageListener in other places
        this.removeMessageListener();
    }
    private _handleLogin() {
        this.removeMessageListener();
        this.addMessageListener();
    }

    private _handlePendingDisposition(data: any) {
        if (this._options.disabledCall) return;
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handlePendingDisposition] uii: ${data.uii}`);
        this._dispatch(
            updateSecondIframeScript({
                renderId: data.uii,
                submitted: true,
            })
        );
    }

    private _handleChatDispResponse(data: any) {
        if (this._options.disableDigital) return;
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleChatDispResponse] uii: ${data.uii}`);
        this._dispatch(setSecondIframeDigital(null));
        this._chatMap.delete(data.uii);
        this._onDigitalStop();
    }

    private _handleStopRender() {
        RCXLogger.getInstance().info(`${LOG_PREFIX}[_handleStopRender]`);
        this._dispatch(setSecondIframeScript(null));
        this._dispatch(updateSecondIframeLoading(false));
    }

    private handleMessageFromSecondIframe = (event: { data: MessageEventData<any> }) => {
        const { type, data } = event.data;
        if (type !== SECOND_IFRAME_MESSAGE_TYPE) return;
        // do not log to indexdb
        console.log('===== handleMessageFromSecondIframe', event.data, this._curScriptRenderId);
        this._dispatch(updateSecondIframeLoading(false));
        switch (data?.execute) {
            case CRM_ACTIONS.SCRIPT_ON_ERROR:
                RCXLogger.getInstance().info(`${LOG_PREFIX}[handleMessageFromSecondIframe]:${data.execute}`);
                this._handleScriptError();
                break;
            default:
                break;
        }
    };

    private _handleScriptError() {
        // @ts-ignore
        window[Symbol.for('rcx_sentry')]?.captureException({
            error: new Error('Script token invalid'),
        });
    }

    private _handleDigitalMessage = (event: MessageEvent) => {
        if (event.data.name === DigitalMessageType.NEW_MESSAGE) {
            this._options?.openCTIApp?.();
        }
    };

    private _onDigitalStart() {
        window.addEventListener('message', this._handleDigitalMessage.bind(this));
    }

    private _onDigitalStop() {
        window.removeEventListener('message', this._handleDigitalMessage.bind(this));
    }

    private _addErrorListener() {
        window.removeEventListener('error', this._handleError);
        window.removeEventListener('unhandledrejection', this._unhandledrejection);
        window.addEventListener('error', this._handleError);
        window.addEventListener('unhandledrejection', this._unhandledrejection);
    }

    private _handleError(event: ErrorEvent) {
        event.preventDefault?.();
        const eventData = event.error?.stack || event.message;
        const action = eventData?.action || '';
        const error = eventData?.error || '';
        // @ts-ignore
        window[Symbol.for('rcx_sentry')]?.captureException({
            action,
            error: new Error(error),
        });
    }

    private _unhandledrejection(event: PromiseRejectionEvent) {
        event.preventDefault?.();
        const action = event?.reason?.stack?.action || '';
        const error = event?.reason?.stack?.error || '';
        // @ts-ignore
        window[Symbol.for('rcx_sentry')]?.captureException({
            action,
            error: new Error(error),
        });
    }

    public addMessageListener() {
        window.removeEventListener('message', this.handleMessageFromSecondIframe);
        window.addEventListener('message', this.handleMessageFromSecondIframe);
    }

    public removeMessageListener() {
        window.removeEventListener('message', this.handleMessageFromSecondIframe);
        window.removeEventListener('error', this._handleError);
        window.removeEventListener('unhandledrejection', this._unhandledrejection);
    }
    public addAllMessageListener() {
        this.removeMessageListener();
        this.addMessageListener();
        this._addErrorListener();
    }

    public sendViaIframeMessage = (data: any, iframeWindow: Window) => {
        const payload = JSON.parse(
            JSON.stringify({
                type: SECOND_IFRAME_MESSAGE_TYPE,
                data,
            })
        );
        iframeWindow?.postMessage(payload, '*');
    };

    public setScriptWindow(scriptWindow: HTMLIFrameElement) {
        this._scriptWindow = scriptWindow.contentWindow!;
    }
    public expandSecondIFrame() {
        this.analyticsTrack('RCX_expand_2ndiframe_sf_drag', { type: this._curType });
        this._dispatch(changeSecondIframeState(SecondIframeState.Expand));
    }

    public rerenderScript(scriptId: string) {
        if (scriptId) {
            this.sendViaIframeMessage(
                {
                    execute: CRM_ACTIONS.SCRIPT_RERENDER,
                    scriptId: scriptId,
                },
                this._scriptWindow
            );
        }
    }

    public resetState() {
        RCXLogger.getInstance().info(`${LOG_PREFIX}[resetState]`);
        this._chatMap.clear();
        this._dispatch(resetSecondIframeState());
        this._dispatch(updateSecondIframeLoading(false));
    }
}
